import { useEffect, useState } from "react";
import { NeuralSidebar } from "@/components/neural/NeuralSidebar";
import { NeuralTopBar } from "@/components/neural/NeuralTopBar";
import { NeuralStatusBar } from "@/components/neural/NeuralStatusBar";
import { NeuralChat } from "@/components/neural/NeuralChat";
import { TransparencyPanel } from "@/components/neural/TransparencyPanel";
import { AgentCanvas } from "@/components/neural/AgentCanvas";
import { Dashboard } from "@/components/neural/Dashboard";
import { MemoryUniverse } from "@/components/neural/MemoryUniverse";
import { AgentsHub } from "@/components/neural/AgentsHub";
import { ToolsGalaxy } from "@/components/neural/ToolsGalaxy";
import { Scheduler } from "@/components/neural/Scheduler";
import { Sandbox } from "@/components/neural/Sandbox";
import { SecondBrain } from "@/components/neural/SecondBrain";
import { CommandPalette } from "@/components/neural/CommandPalette";
import { Marketplace } from "@/components/neural/Marketplace";
import { Governance } from "@/components/neural/Governance";
import { DevOps } from "@/components/neural/DevOps";
import { BuilderStudio } from "@/components/neural/BuilderStudio";
import { Integrations } from "@/components/neural/Integrations";
import { WorkflowMode } from "@/components/neural/modes/WorkflowMode";
import { GameMode } from "@/components/neural/modes/GameMode";
import { SpaceMode } from "@/components/neural/modes/SpaceMode";
import { MissionControlMode } from "@/components/neural/modes/MissionControlMode";
import { useNeural } from "@/store/neuralStore";
import { toast } from "sonner";
import { MobileShell } from "@/mobile/MobileShell";
import { useIsMobile, isNativePlatform } from "@/mobile/platform";

const Index = () => {
  const [section, setSection] = useState("chat");
  const [collapsed, setCollapsed] = useState(false);
  const mode = useNeural((s) => s.mode);
  const setMode = useNeural((s) => s.setMode);
  const isMobile = useIsMobile();
  const useMobileShell = isMobile || isNativePlatform();

  const handleSelect = (id: string) => {
    setSection(id);
    // Sidebar navigation always returns to classic so the chosen page is visible
    if (mode !== "classic") setMode("classic");
  };

  useEffect(() => {
    const onNav = (e: any) => {
      setSection(e.detail);
      if (useNeural.getState().mode !== "classic") useNeural.getState().setMode("classic");
    };
    const onAct = (e: any) => {
      if (e.detail === "pause-all") toast("⏸ All agents paused");
      if (e.detail === "refresh-models") {
        useNeural.getState().refreshModels();
        toast.success("Models refreshed");
      }
    };
    window.addEventListener("neural-nav", onNav as any);
    window.addEventListener("neural-action", onAct as any);
    return () => {
      window.removeEventListener("neural-nav", onNav as any);
      window.removeEventListener("neural-action", onAct as any);
    };
  }, []);

  const renderClassicSection = () => {
    switch (section) {
      case "dashboard":   return <Dashboard />;
      case "canvas":      return <AgentCanvas />;
      case "memory":      return <MemoryUniverse />;
      case "brain":       return <SecondBrain />;
      case "agents":      return <AgentsHub />;
      case "tools":       return <ToolsGalaxy />;
      case "scheduler":   return <Scheduler />;
      case "sandbox":     return <Sandbox />;
      case "workflows":   return <WorkflowMode />;
      case "marketplace": return <Marketplace />;
      case "governance":  return <Governance />;
      case "history":     return <Governance />;
      case "devops":      return <DevOps />;
      case "builder":     return <BuilderStudio />;
      case "integrations":return <Integrations />;
      case "settings":    return <Integrations />;
      case "chat":
      default:            return <NeuralChat />;
    }
  };

  const renderMain = () => {
    switch (mode) {
      case "game":     return <GameMode />;
      case "workflow": return <WorkflowMode />;
      case "space":    return <SpaceMode />;
      case "mission":  return <MissionControlMode />;
      default:         return renderClassicSection();
    }
  };

  const showTransparency =
    mode === "classic" && (section === "chat" || section === "canvas" || section === "agents" || section === "workflows");

  const titles: Record<string, { t: string; s: string }> = {
    chat:        { t: "Universal Chat",   s: "ensemble multi-model · live judge · auditable" },
    agents:      { t: "Agents Hub",       s: "multi-model per agent · ensemble per-agent" },
    tools:       { t: "Tools Galaxy",     s: "25 multi-agent tools · sandboxed · SHA-512" },
    memory:      { t: "Memory Universe",  s: "tiered memory · short-term, vector, quantum" },
    brain:       { t: "Second Brain",     s: "long-term knowledge · semantic search" },
    scheduler:   { t: "Scheduler",        s: "cron-like task scheduling · DB persisted" },
    sandbox:     { t: "Sandbox",          s: "isolated tool exec · audit trail" },
    canvas:      { t: "Agent Canvas 4D",  s: "live mesh · particles · zoom" },
    dashboard:   { t: "Dashboard",        s: "performance & predictive insights" },
    workflows:   { t: "Workflows Forge",  s: "React Flow · drag & connect agents" },
    marketplace: { t: "Marketplace",      s: "browse · install · publish community items" },
    governance:  { t: "Governance",       s: "audit log · policies · compliance" },
    devops:      { t: "DevOps Studio",    s: "live telemetry · errors · cost · latency" },
    builder:     { t: "Builder Studio",   s: "compose new agents visually" },
    integrations:{ t: "Integrations",      s: "Slack · Gmail · connectors via Lovable Gateway" },
  };
  const showHeader = mode === "classic" && titles[section];

  if (useMobileShell) return <MobileShell />;

  return (
    <div className="h-screen w-screen flex flex-col overflow-hidden bg-background text-foreground">
      <CommandPalette />
      <div className="flex flex-1 overflow-hidden">
        <NeuralSidebar
          active={section}
          onSelect={handleSelect}
          collapsed={collapsed}
          onToggle={() => setCollapsed((v) => !v)}
        />

        <div className="flex-1 flex flex-col overflow-hidden">
          <NeuralTopBar />

          <main className="flex-1 flex overflow-hidden">
            <section className="flex-1 overflow-hidden relative flex flex-col">
              {showHeader && (
                <div className="px-6 pt-3 pb-2 border-b border-border/50 shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-display font-semibold">{titles[section].t}</h2>
                      <p className="text-xs text-muted-foreground">{titles[section].s}</p>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-mono">
                      <span className="px-2 py-0.5 rounded bg-success/10 text-success border border-success/30">● recording</span>
                      <span className="px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/30">audited</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex-1 min-h-0">{renderMain()}</div>
            </section>

            {showTransparency && <TransparencyPanel />}
          </main>
        </div>
      </div>

      <NeuralStatusBar />
    </div>
  );
};

export default Index;
